import React, { useMemo, useState } from 'react';
import classnames from 'classnames';

import { ICoreContext } from '@msdyn365-commerce/core-internal';

import { Button } from '../../../components';
import { useAddToCartDatalayer } from '../../../integrations/datalayer/hooks/useAddToCartDatalayer';
import { useMappingService } from '../../../integrations/relewise/hooks/serviceHooks';
import { useProductDetails } from '../../../integrations/relewise/hooks/useProductDetails';
import { ProductColor } from '../../../integrations/relewise/types/mapped';
import { sanitizeHtmlId } from '../../functions/string-transformers';
import { Drawer } from '../drawer/drawer';
import { ImageWithLoading } from '../image-with-loading/image-with-loading';
import { InfoTooltip } from '../info-tooltip/info-tooltip';
import { useAddToCart } from '../../hooks/useAddToCart';
import { useAvailabilityByColorAndSize } from '../../hooks/useAvailabilityByColorAndSize';
import { useImageSettings } from '../../hooks/useImageSettings';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
    ADD_TO_CART_DRAWER_IMAGE_VIEWPORT_SETTINGS,
    ADD_TO_CART_DRAWER_NAME,
    EMPTY_ADD_TO_CART_SELECTION,
    SKELETON_NUMBER_OF_OPTIONS_TO_SHOW
} from './add-to-cart-drawer.const';
import { AddToCartDrawerLabels, AddToCartSelection } from './add-to-cart-drawer.types';
import { ProductSkeleton } from './components/product-skeleton/product-skeleton';
import { ColorOptionsSkeleton } from './components/color-options-skeleton/color-options-skeleton';
import { SizeOptionsSkeleton } from './components/size-options-skeleton/size-options-skeleton';
import { getActiveSelection } from './helpers/getActiveSelection';
import { getSelectedVariant } from './helpers/getSelectedVariant';
import { useResetAddToCartDrawer } from './hooks/useResetAddToCartDrawer';

interface Props {
    masterProductRecordId: number | undefined;
    productName: string;
    isOpen: boolean;
    selectedColor?: string;
    onClose: () => void;
    context: ICoreContext;
    labels: AddToCartDrawerLabels;
}
export const AddToCartDrawer = ({ masterProductRecordId, productName, isOpen, selectedColor, onClose, context, labels }: Props) => {
    const imageSettings = useImageSettings(ADD_TO_CART_DRAWER_IMAGE_VIEWPORT_SETTINGS);
    const relewiseMappingService = useMappingService(context.request, context.telemetry);
    const isMobile = useIsMobile(context);

    const [selection, setSelection] = useState<AddToCartSelection>(EMPTY_ADD_TO_CART_SELECTION);

    const {
        productDetails,
        isLoading: isProductDetailsLoading,
        hasError: hasProductDetailsError
    } = useProductDetails({
        productRecordId: masterProductRecordId,
        context,
        location: ADD_TO_CART_DRAWER_NAME
    });
    const { allColors, variantsByKey, compatibleSizesByColor, productItemId, productRecordId } = productDetails ?? {};

    const { activeColor, activeSize, sizesForActiveColor, showSizeRequiredError } = getActiveSelection({
        selection,
        masterProductRecordId,
        preferredColorName: selectedColor,
        allColors,
        compatibleSizesByColor
    });

    const selectedVariant = useMemo(
        () => getSelectedVariant({ productItemId, variantsByKey, compatibleSizesByColor, activeColor, activeSize, relewiseMappingService }),
        [productItemId, variantsByKey, compatibleSizesByColor, activeColor, activeSize, relewiseMappingService]
    );

    const {
        currentSelectionOutOfStock,
        availabilityByColor,
        availabilityForSelectedColorBySize,
        isLoading: isAvailabilityLoading
    } = useAvailabilityByColorAndSize({
        context,
        variantsByKey,
        allColors,
        selectedVariant: selectedVariant,
        selectedColor: activeColor,
        selectedSize: activeSize,
        compatibleSizesByColor,
        productItemId,
        moduleName: ADD_TO_CART_DRAWER_NAME
    });

    const {
        handleAddToCartClick,
        addToCartLoading,
        hasError: hasAddToCartError,
        clearError: clearAddToCartError
    } = useAddToCart({
        context,
        selectedVariant: selectedVariant,
        productItemId,
        productRecordId,
        unitOfMeasure: productDetails?.unitOfMeasure,
        telemetry: context.telemetry,
        moduleName: ADD_TO_CART_DRAWER_NAME
    });

    useResetAddToCartDrawer(isOpen, setSelection, clearAddToCartError);

    const trackAddToCart = useAddToCartDatalayer({
        request: context.request,
        telemetry: context.telemetry,
        productItemId,
        selectedVariant,
        selectedColor: activeColor,
        selectedSize: activeSize,
        brand: productDetails?.brand
    });

    const handleColorSelect = (color: ProductColor) => {
        if (color.name === activeColor?.name) {
            return;
        }

        setSelection({
            productRecordId: masterProductRecordId,
            color,
            size: undefined,
            sizeRequiredError: showSizeRequiredError
        });
    };

    const handleSizeSelect = (size: string) => {
        setSelection({ productRecordId: masterProductRecordId, color: activeColor, size, sizeRequiredError: false });
    };

    const handleAddToCart = async () => {
        if (!activeSize) {
            setSelection({ productRecordId: masterProductRecordId, color: activeColor, size: undefined, sizeRequiredError: true });
            return;
        }
        const { success } = await handleAddToCartClick();
        if (success) {
            trackAddToCart();
            onClose();
        }
    };

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title={labels.title}
            closeButtonLabel={labels.closeButtonLabel}
            className='bs-add-to-cart-drawer'
        >
            <div className='bs-add-to-cart-drawer__content'>
                {hasProductDetailsError ? (
                    <div className='bs-add-to-cart-drawer__error' role='alert'>
                        {labels.loadingErrorLabel}
                    </div>
                ) : (
                    <>
                        {isProductDetailsLoading ? (
                            <ProductSkeleton productName={productName} />
                        ) : (
                            productDetails && (
                                <div className='bs-add-to-cart-drawer__product'>
                                    <ImageWithLoading
                                        wrapperClassname='bs-add-to-cart-drawer__image-wrapper'
                                        src={selectedVariant?.primaryImageUrl}
                                        alt={productName}
                                        title={productName}
                                        imageSettings={{ viewports: imageSettings }}
                                        gridSettings={context.request.gridSettings ?? {}}
                                        loadFailureBehavior='empty'
                                        showEmptyImageThumbnail
                                        location={ADD_TO_CART_DRAWER_NAME}
                                        telemetry={context.telemetry}
                                        variantConcatenatedId={selectedVariant?.primarySizeVariant?.concatenatedId}
                                    />
                                    <div className='bs-add-to-cart-drawer__product-info'>
                                        <span className='bs-add-to-cart-drawer__product-name'>{productName}</span>
                                        <span className='bs-add-to-cart-drawer__price'>
                                            <span
                                                className={classnames('bs-add-to-cart-drawer__final-price', {
                                                    'bs-add-to-cart-drawer__final-price--discounted': selectedVariant?.hasDiscount
                                                })}
                                            >
                                                {selectedVariant?.hasMemberPrice
                                                    ? selectedVariant.basePriceFormatted
                                                    : selectedVariant?.finalPriceFormatted}
                                            </span>
                                            {selectedVariant?.hasDiscount && (
                                                <span className='bs-add-to-cart-drawer__original-price'>
                                                    {selectedVariant.basePriceFormatted}
                                                </span>
                                            )}
                                        </span>
                                        {isAvailabilityLoading ? (
                                            <ColorOptionsSkeleton count={allColors?.length ?? 0} />
                                        ) : (
                                            <ul
                                                className='bs-add-to-cart-drawer__color-options'
                                                role='radiogroup'
                                                aria-label={labels.colorSelectionLabel}
                                            >
                                                {allColors?.map(color => {
                                                    const doesColorHaveStock = availabilityByColor ? availabilityByColor[color.name] : true;
                                                    const tooltipId = sanitizeHtmlId(`add-to-cart-drawer-color-${color.name}`);
                                                    return (
                                                        <li key={color.name}>
                                                            <InfoTooltip id={tooltipId} isMobile={isMobile} content={color.name}>
                                                                <button
                                                                    type='button'
                                                                    className={classnames('bs-add-to-cart-drawer__color-item', {
                                                                        'bs-add-to-cart-drawer__color-item--selected':
                                                                            activeColor?.name === color.name,
                                                                        'bs-add-to-cart-drawer__color-item--out-of-stock':
                                                                            !doesColorHaveStock
                                                                    })}
                                                                    style={
                                                                        { '--color-hex': color.hex ?? 'transparent' } as React.CSSProperties
                                                                    }
                                                                    onClick={() => handleColorSelect(color)}
                                                                    role='radio'
                                                                    aria-checked={activeColor?.name === color.name}
                                                                    aria-label={color.name}
                                                                />
                                                            </InfoTooltip>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                        {activeColor && (
                                            <span className='bs-add-to-cart-drawer__selected-color-name'>{`${
                                                activeColor.colorRefinerGroup ? `${activeColor.colorRefinerGroup} / ` : ''
                                            }${activeColor.name}`}</span>
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                        <div className='bs-add-to-cart-drawer__footer'>
                            <div className='bs-add-to-cart-drawer__size-header'>
                                <span
                                    className={classnames('bs-add-to-cart-drawer__size-label', {
                                        'bs-add-to-cart-drawer__size-label--error': showSizeRequiredError
                                    })}
                                    role={showSizeRequiredError ? 'alert' : undefined}
                                >
                                    {labels.selectSizeLabel}
                                </span>
                                {currentSelectionOutOfStock && (
                                    <span className='bs-add-to-cart-drawer__out-of-stock' role='alert'>
                                        {labels.outOfStockLabel}
                                    </span>
                                )}
                            </div>
                            {isProductDetailsLoading || isAvailabilityLoading ? (
                                <SizeOptionsSkeleton
                                    count={isProductDetailsLoading ? SKELETON_NUMBER_OF_OPTIONS_TO_SHOW : sizesForActiveColor.length}
                                />
                            ) : (
                                <ul className='bs-add-to-cart-drawer__size-options' role='radiogroup' aria-label={labels.selectSizeLabel}>
                                    {sizesForActiveColor.map(size => {
                                        const doesSizeHaveStock = availabilityForSelectedColorBySize
                                            ? availabilityForSelectedColorBySize[size]
                                            : true;
                                        return (
                                            <li key={size} className='bs-add-to-cart-drawer__size-option'>
                                                <button
                                                    type='button'
                                                    className={classnames('bs-add-to-cart-drawer__size-item', {
                                                        'bs-add-to-cart-drawer__size-item--selected': activeSize === size,
                                                        'bs-add-to-cart-drawer__size-item--out-of-stock': !doesSizeHaveStock,
                                                        'bs-add-to-cart-drawer__size-item--error': showSizeRequiredError
                                                    })}
                                                    onClick={() => handleSizeSelect(size)}
                                                    role='radio'
                                                    aria-checked={activeSize === size}
                                                >
                                                    {size}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            <Button
                                className='bs-add-to-cart-drawer__add-to-cart'
                                disabled={
                                    isProductDetailsLoading ||
                                    isAvailabilityLoading ||
                                    currentSelectionOutOfStock ||
                                    (!!activeSize && !selectedVariant)
                                }
                                isLoading={addToCartLoading}
                                onClick={handleAddToCart}
                            >
                                {labels.addToCartLabel}
                            </Button>
                            {hasAddToCartError && <div className='bs-add-to-cart-drawer__error'>{labels.addToCartErrorLabel}</div>}
                            <Button
                                className='bs-add-to-cart-drawer__go-to-product'
                                mode='anchor'
                                variant='secondary'
                                href={selectedVariant?.variantFullUrl}
                            >
                                {labels.goToProductLabel}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Drawer>
    );
};
